import { organizerRouteNames } from '../../../../routing/routeNames/organizerRouteNames'

const {
  eventInfo,
  eventWorkers,
  eventCategories,
  accreditations,
  requests,
  createAccreditation,
  editAccreditation,
  accreditationBuilder,
  eventDashboard,
  inviteAttendee,
  emailsToInvitees,
  curatedInvitations,
  createEmailInvitation,
  eventWorkshops,
  eventTranslate,
  newWorkshop,
  updateWorkshop,
  scanEntries,
  attendeeFields,
  eventConferenceData,
  eventAnnouncements,
  newAnnouncement,
  displayAnnouncement,
  eventHalls,
  newHall,
  updateHall,
  importExport,
  import: importPage,
  export: exportPage,
  editInvite,
  emails,
  createEmail,
  emailDetails,
  updateEmail,
  tickets,
  floorPlan,
  visitorAnalytics,
  ticketStatistics,
  visitorStatistics,
  scannedTickets,
  createTicket,
  editTicket,
  addTicket,
  floorPlanOverview,
  documentation,
  eventExtraModules,
} = organizerRouteNames

/**
 * Sidebar keys for mapping routes to sidebar links
 * Some are string literals because for those links there isn't any route, they are just dropdowns for their subroutes
 *
 * They are in order as they are in Sidebar
 */
const sidebarKeys = {
  dashboard: eventDashboard,
  attendees: 'attendees',
  attendeeList: accreditations,
  importExport: importExport,
  emailsToAttendees: emails,
  eventEntries: scanEntries,
  inviteShare: 'invite-share',
  inviteAttendees: inviteAttendee,
  pendingRequests: requests,
  customizeInvite: editInvite,
  designTool: accreditationBuilder,
  ticketing: 'ticketing',
  tickets: tickets,
  floorPlan: floorPlan,
  eventSettings: 'event-settings',
  eventInfo: eventInfo,
  eventExtraModules: eventExtraModules,
  eventWorkers: eventWorkers,
  categoriesAndZones: eventCategories,
  attendeeFields: attendeeFields,
  workshops: eventWorkshops,
  translate: eventTranslate,
  conferenceSettings: 'conference-settings',
  conferenceInfo: eventConferenceData,
  announcements: eventAnnouncements,
  visitorAnalytics: visitorAnalytics,
  halls: eventHalls,
  visitorStatistics: visitorStatistics,
  scannedTickets: scannedTickets,
  ticketStatistics: ticketStatistics,
  floorPlanOverview: floorPlanOverview,
  documentation: documentation,
}

/**
 * Sidebar links - objects for sidebar configuration
 *
 * Type:
 * {
 *    label: Name displayed in sidebar
 *    icon: Name of icon, representing the one inside assets/sidebar-icons
 *    key: Key for mapping the link to sidebar, take from sidebarKeys
 *    modulesRequired?: List of modules required for displaying this link
 *    allowedRoles?: List of roles for which this link will be displayed
 *    linkedRoutes: List of all routes for which this link will be highlighted as active and disabled for clicking (including its internal route)
 *    internalRoute: {
 *      name: Name of route this link will lead to, take from routeNames object
 *    }
 *    subroutes? List of route objects, only dropdowns have this property
 * }
 */
export const organizerSidebarConfiguration = [
  {
    label: 'Dashboard',
    icon: 'dashboard',
    key: sidebarKeys.dashboard,
    subroutes: [
      {
        label: 'Ticket statistics',
        key: sidebarKeys.dashboard,
        linkedRoutes: [eventDashboard],
        internalRoute: {
          name: organizerRouteNames.eventDashboard,
        },
      },
      // {
      //   label: 'Ticket statistics 2',
      //   key: sidebarKeys.ticketStatistics,
      //   linkedRoutes: [ticketStatistics],
      //   internalRoute: {
      //     name: organizerRouteNames.ticketStatistics,
      //   },
      // },
      {
        label: 'Floor plan overview',
        key: sidebarKeys.floorPlanOverview,
        modulesRequired: ['floorPlan'],
        linkedRoutes: [floorPlanOverview],
        internalRoute: {
          name: organizerRouteNames.floorPlanOverview,
        },
      },
      {
        label: 'Visitor statistics',
        key: sidebarKeys.visitorStatistics,
        linkedRoutes: [visitorStatistics],
        internalRoute: {
          name: organizerRouteNames.visitorStatistics,
        },
      },
      {
        label: 'Scanned tickets',
        key: sidebarKeys.scannedTickets,
        linkedRoutes: [scannedTickets],
        internalRoute: {
          name: organizerRouteNames.scannedTickets,
        },
      },
    ],
    modulesRequired: ['ticketing'],
    linkedRoutes: [eventDashboard],
    internalRoute: {
      name: organizerRouteNames.eventDashboard,
    },
  },
  {
    label: 'Attendees',
    icon: 'people',
    key: sidebarKeys.attendees,
    subroutes: [
      {
        label: 'Attendee list',
        key: sidebarKeys.attendeeList,
        linkedRoutes: [accreditations, createAccreditation, editAccreditation, addTicket],
        internalRoute: {
          name: organizerRouteNames.accreditations,
        },
      },
      {
        label: 'Import/Export',
        key: sidebarKeys.importExport,
        linkedRoutes: [importExport, importPage, exportPage],
        internalRoute: {
          name: organizerRouteNames.importExport,
        },
      },
      {
        label: 'E-mails to attendees',
        key: sidebarKeys.emailsToAttendees,
        linkedRoutes: [emails, createEmail, updateEmail, emailDetails],
        internalRoute: {
          name: organizerRouteNames.emails,
        },
      },
      {
        label: 'Event entries',
        key: sidebarKeys.eventEntries,
        modulesRequired: ['scanEntries'],
        linkedRoutes: [scanEntries],
        internalRoute: {
          name: organizerRouteNames.scanEntries,
        },
      },
    ],
  },
  {
    label: 'Invite & share',
    icon: 'share',
    key: sidebarKeys.inviteShare,
    subroutes: [
      {
        label: 'Invite attendees',
        key: sidebarKeys.inviteAttendees,
        linkedRoutes: [inviteAttendee],
        internalRoute: {
          name: organizerRouteNames.inviteAttendee,
        },
      },
      {
        label: 'Pending requests',
        key: sidebarKeys.pendingRequests,
        linkedRoutes: [requests],
        internalRoute: {
          name: organizerRouteNames.requests,
        },
        requestCount: true,
      },
      {
        label: 'Customize invite',
        key: sidebarKeys.customizeInvite,
        linkedRoutes: [editInvite],
        internalRoute: {
          name: organizerRouteNames.editInvite,
        },
      },
      {
        label: 'Curated invitations',
        key: curatedInvitations,
        linkedRoutes: [curatedInvitations],
        internalRoute: {
          name: curatedInvitations,
        },
      },
      {
        label: 'Create e-mail invitation',
        key: createEmailInvitation,
        linkedRoutes: [createEmailInvitation],
        internalRoute: {
          name: createEmailInvitation,
        },
      },
      {
        label: 'Emails to invitees',
        key: emailsToInvitees,
        linkedRoutes: [emailsToInvitees],
        internalRoute: {
          name: emailsToInvitees,
        },
      },
    ],
  },
  {
    label: 'Design tool',
    icon: 'brush',
    key: sidebarKeys.designTool,
    linkedRoutes: [accreditationBuilder],
    internalRoute: {
      name: organizerRouteNames.accreditationBuilder,
    },
  },
  // {
  //   label: 'Visitor analytics',
  //   icon: 'filter_list',
  //   key: sidebarKeys.visitorAnalytics,
  //   linkedRoutes: [visitorAnalytics],
  //   internalRoute: {
  //     name: organizerRouteNames.visitorAnalytics,
  //   },
  // },
  {
    label: 'Ticketing',
    icon: 'tickets',
    key: sidebarKeys.ticketing,
    modulesRequired: ['ticketing'],
    subroutes: [
      {
        label: 'Tickets',
        key: sidebarKeys.tickets,
        linkedRoutes: [tickets, createTicket, editTicket],
        internalRoute: {
          name: organizerRouteNames.tickets,
        },
      },
      {
        label: 'Floor plan',
        key: sidebarKeys.floorPlan,
        modulesRequired: ['floorPlan'],
        linkedRoutes: [floorPlan],
        internalRoute: {
          name: organizerRouteNames.floorPlan,
        },
      },
    ],
  },
  {
    label: 'Event settings',
    icon: 'settings',
    key: sidebarKeys.eventSettings,
    subroutes: [
      {
        label: 'Event info',
        key: sidebarKeys.eventInfo,
        linkedRoutes: [eventInfo],
        internalRoute: {
          name: organizerRouteNames.eventInfo,
        },
      },
      {
        label: 'Modules',
        key: sidebarKeys.eventExtraModules,
        linkedRoutes: [eventExtraModules],
        internalRoute: {
          name: organizerRouteNames.eventExtraModules,
        },
      },
      {
        label: 'Event workers',
        key: sidebarKeys.eventWorkers,
        linkedRoutes: [eventWorkers],
        internalRoute: {
          name: organizerRouteNames.eventWorkers,
        },
      },
      {
        label: 'Categories & zones',
        key: sidebarKeys.categoriesAndZones,
        modulesRequired: ['categoriesAndZones'],
        linkedRoutes: [eventCategories],
        internalRoute: {
          name: organizerRouteNames.eventCategories,
        },
      },
      {
        label: 'Attendee Fields',
        key: sidebarKeys.attendeeFields,
        linkedRoutes: [attendeeFields],
        internalRoute: {
          name: organizerRouteNames.attendeeFields,
        },
      },
      {
        label: 'Workshops',
        key: sidebarKeys.workshops,
        modulesRequired: ['workshops', 'conferenceMobileApp'],
        linkedRoutes: [eventWorkshops, newWorkshop, updateWorkshop],
        internalRoute: {
          name: organizerRouteNames.eventWorkshops,
        },
      },
      {
        label: 'Translate attendee fields',
        key: sidebarKeys.translate,
        linkedRoutes: [eventTranslate],
        internalRoute: {
          name: organizerRouteNames.eventTranslate,
        },
      },
    ],
  },
  {
    label: 'Conference settings',
    icon: 'settings',
    key: sidebarKeys.conferenceSettings,
    modulesRequired: ['conferenceMobileApp'],
    subroutes: [
      {
        label: 'Conference Info',
        key: sidebarKeys.conferenceInfo,
        linkedRoutes: [eventConferenceData],
        internalRoute: {
          name: organizerRouteNames.eventConferenceData,
        },
      },
      {
        label: 'Announcements',
        key: sidebarKeys.announcements,
        linkedRoutes: [eventAnnouncements, newAnnouncement, displayAnnouncement],
        internalRoute: {
          name: organizerRouteNames.eventAnnouncements,
        },
      },
      {
        label: 'Halls',
        key: sidebarKeys.halls,
        linkedRoutes: [eventHalls, newHall, updateHall],
        internalRoute: {
          name: organizerRouteNames.eventHalls,
        },
      },
    ],
  },
  {
    label: 'Documentation',
    icon: 'documentation',
    key: sidebarKeys.documentation,
    modulesRequired: ['ticketing'],
    linkedRoutes: [organizerRouteNames.eventDocumentation],
    internalRoute: {
      name: organizerRouteNames.eventDocumentation,
    },
  },
]
